import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { 
  usePendingUsers, 
  useApprovePendingUser, 
  useRejectPendingUser,
  useBulkApprovePendingUsers 
} from '../hooks/usePendingUsers';
import { 
  UserPlus, 
  UserCheck, 
  UserX, 
  Mail, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Search,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import type { PendingUser } from '../api/pendingUsers';

export const PendingUsersPage: React.FC = () => {
  const { theme } = useTheme();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [rejectingUser, setRejectingUser] = useState<PendingUser | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const { data, isLoading, error, refetch } = usePendingUsers(page, pageSize, search);
  const approveMutation = useApprovePendingUser();
  const rejectMutation = useRejectPendingUser();
  const bulkApproveMutation = useBulkApprovePendingUsers();

  const users = data?.data || [];
  const pagination = data?.pagination;

  const handleSelectUser = (userId: string) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(userId)) {
      newSelected.delete(userId);
    } else {
      newSelected.add(userId);
    }
    setSelectedUsers(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedUsers.size === users.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(users.map(u => u.id)));
    }
  };

  const handleApprove = async (userId: string) => {
    try {
      await approveMutation.mutateAsync(userId);
      alert('User approved successfully! Activation email sent.');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to approve user');
    }
  };

  const handleReject = async () => {
    if (!rejectingUser) return;

    try {
      await rejectMutation.mutateAsync({ 
        userId: rejectingUser.id, 
        reason: rejectionReason 
      });
      setRejectingUser(null);
      setRejectionReason('');
      alert('User rejected successfully');
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to reject user');
    }
  };

  const handleBulkApprove = async () => {
    if (selectedUsers.size === 0) {
      alert('Please select users to approve');
      return;
    }

    if (!confirm(`Approve ${selectedUsers.size} user(s)?`)) {
      return;
    }

    try {
      const result = await bulkApproveMutation.mutateAsync(Array.from(selectedUsers));
      setSelectedUsers(new Set());
      alert(`Approved ${result.successCount} user(s). ${result.failureCount} failed.`);
    } catch (error: any) {
      alert(error.response?.data?.error || 'Failed to bulk approve users');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className={`px-8 py-6 border-b ${
        theme === 'light' 
          ? 'bg-white border-gray-200' 
          : 'bg-slate-800 border-slate-700'
      }`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-2xl font-bold ${
              theme === 'light' ? 'text-gray-900' : 'text-white'
            }`}>
              Pending User Approvals
            </h1>
            <p className={`mt-1 text-sm ${
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            }`}>
              Review and approve user registrations
            </p>
          </div>

          {selectedUsers.size > 0 && (
            <button
              onClick={handleBulkApprove}
              disabled={bulkApproveMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
            >
              <UserCheck className="w-4 h-4" />
              Approve Selected ({selectedUsers.size})
            </button>
          )}
        </div>

        {/* Search */}
        <div className="mt-4">
          <div className="relative">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 ${
              theme === 'light' ? 'text-gray-400' : 'text-gray-500'
            }`} />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className={`w-full pl-10 pr-4 py-2 rounded-lg border ${
                theme === 'light'
                  ? 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                  : 'bg-slate-700 border-slate-600 text-white placeholder-gray-400'
              } focus:outline-none focus:ring-2 focus:ring-cyan-500`}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className={`text-lg ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>
              Loading pending users...
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-red-500">Error loading users</div>
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <UserPlus className={`w-16 h-16 mb-4 ${
              theme === 'light' ? 'text-gray-400' : 'text-gray-600'
            }`} />
            <div className={`text-lg font-medium ${
              theme === 'light' ? 'text-gray-900' : 'text-white'
            }`}>
              No pending users
            </div>
            <div className={`mt-2 text-sm ${
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            }`}>
              All user registrations have been processed
            </div>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className={`rounded-lg border ${
              theme === 'light' 
                ? 'bg-white border-gray-200' 
                : 'bg-slate-800 border-slate-700'
            } overflow-hidden`}>
              <table className="w-full">
                <thead className={theme === 'light' ? 'bg-gray-50' : 'bg-slate-700'}>
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedUsers.size === users.length && users.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300"
                      />
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                    }`}>
                      User
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                    }`}>
                      Contact
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                    }`}>
                      Signup Date
                    </th>
                    <th className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider ${
                      theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                    }`}>
                      Assignment
                    </th>
                    <th className={`px-4 py-3 text-right text-xs font-medium uppercase tracking-wider ${
                      theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                    }`}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${
                  theme === 'light' ? 'divide-gray-200' : 'divide-slate-700'
                }`}>
                  {users.map((user) => (
                    <tr key={user.id} className={
                      theme === 'light' ? 'hover:bg-gray-50' : 'hover:bg-slate-700/50'
                    }>
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(user.id)}
                          onChange={() => handleSelectUser(user.id)}
                          className="rounded border-gray-300"
                        />
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-purple-500 flex items-center justify-center text-white font-semibold">
                            {user.firstName[0]}{user.lastName[0]}
                          </div>
                          <div>
                            <div className={`font-medium ${
                              theme === 'light' ? 'text-gray-900' : 'text-white'
                            }`}>
                              {user.fullName}
                            </div>
                            <div className={`text-sm ${
                              theme === 'light' ? 'text-gray-500' : 'text-gray-400'
                            }`}>
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className={`text-sm ${
                          theme === 'light' ? 'text-gray-900' : 'text-gray-300'
                        }`}>
                          {user.phone || 'N/A'}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className={`text-sm ${
                          theme === 'light' ? 'text-gray-900' : 'text-gray-300'
                        }`}>
                          {formatDate(user.signupDate)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        {user.hasRoleAssignment ? (
                          <div className="flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="w-4 h-4" />
                            <span className="text-sm">
                              {user.tenant?.name} - {user.roles.length} role(s)
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 text-amber-600">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm">No assignment</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleApprove(user.id)}
                            disabled={!user.hasRoleAssignment || approveMutation.isPending}
                            title={user.hasRoleAssignment ? 'Approve and send activation email' : 'Assign tenant and role first'}
                            className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white text-sm rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <UserCheck className="w-4 h-4" />
                            Approve
                          </button>
                          <button
                            onClick={() => setRejectingUser(user)}
                            disabled={rejectMutation.isPending}
                            className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
                          >
                            <UserX className="w-4 h-4" />
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <div className={`text-sm ${
                  theme === 'light' ? 'text-gray-700' : 'text-gray-300'
                }`}>
                  Showing {((page - 1) * pageSize) + 1} to {Math.min(page * pageSize, pagination.total)} of {pagination.total} users
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className={`px-3 py-2 rounded-lg border ${
                      theme === 'light'
                        ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        : 'border-slate-600 bg-slate-700 text-gray-300 hover:bg-slate-600'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setPage(p => p + 1)}
                    disabled={!pagination.hasMore}
                    className={`px-3 py-2 rounded-lg border ${
                      theme === 'light'
                        ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                        : 'border-slate-600 bg-slate-700 text-gray-300 hover:bg-slate-600'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Rejection Modal */}
      {rejectingUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-md w-full rounded-lg p-6 ${
            theme === 'light' ? 'bg-white' : 'bg-slate-800'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              theme === 'light' ? 'text-gray-900' : 'text-white'
            }`}>
              Reject User Registration
            </h3>
            <p className={`mb-4 text-sm ${
              theme === 'light' ? 'text-gray-600' : 'text-gray-400'
            }`}>
              Are you sure you want to reject <strong>{rejectingUser.fullName}</strong>?
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Optional: Reason for rejection..."
              className={`w-full px-3 py-2 rounded-lg border ${
                theme === 'light'
                  ? 'bg-white border-gray-300 text-gray-900'
                  : 'bg-slate-700 border-slate-600 text-white'
              } focus:outline-none focus:ring-2 focus:ring-red-500`}
              rows={3}
            />
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setRejectingUser(null);
                  setRejectionReason('');
                }}
                className={`flex-1 px-4 py-2 rounded-lg border ${
                  theme === 'light'
                    ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    : 'border-slate-600 text-gray-300 hover:bg-slate-700'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={rejectMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {rejectMutation.isPending ? 'Rejecting...' : 'Reject User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
